<template>
	<div class="PaymentLevelsField">
		<div class="PaymentLevelsField__header">
			<h3>{{ $t('text.contractPaymentLevelsTitle') }}</h3>
			<p>{{ $t('text.contractPaymentLevelsInfoText') }}</p>
		</div>

		<v-radio-group
			v-model="paymentLevelModel"
			hide-details
			:disabled="disabled"
			class="PaymentLevelsField__group"
		>
			<div
				v-for="option in paymentLevelOptions"
				:key="option.value"
				class="PaymentLevelsField__option"
				:class="{ 'PaymentLevelsField__option--selected': currentLevel === option.value }"
				@click="!disabled && (paymentLevelModel = option.value)"
			>
				<div class="PaymentLevelsField__content">
					<div class="PaymentLevelsField__content__header">
						<v-radio
							class="PaymentLevelsField__radio"
							:value="option.value"
							:disabled="disabled"
							color="#00aeef"
							hide-details
						/>
					</div>
					<div class="PaymentLevelsField__content__title">
						<div class="PaymentLevelsField__title">{{ $t(option.titleKey) }}</div>
						<div class="PaymentLevelsField__subtitle">{{ $t(option.subtitleKey) }}</div>
					</div>
				</div>
				<div
					v-if="option.hasPaymentTarget"
					class="PaymentLevelsField__target"
					:class="{
						'PaymentLevelsField__target--disabled': currentLevel !== option.value || disabled,
						'PaymentLevelsField__target--error': hasPaymentTargetError(option.value)
					}"
				>
					<div class="PaymentLevelsField__targetTitle">{{ $t('text.contractPaymentLevelsTargetTitle') }}</div>
					<div class="PaymentLevelsField__targetInfo">{{ $t(option.targetInfoKey) }}</div>
					<div class="PaymentLevelsField__numberWrap" @click.stop>
						<NumberField
							class="Field PaymentLevelsField__numberField"
							:class="{ 'PaymentLevelsField__numberField--error': hasPaymentTargetError(option.value) }"
							:model-value="invoiceDueDaysForLevel(option.value)"
							:unit="$t('text.days').toLowerCase()"
							:min="1"
							:integerOnly="true"
							:placeholder="0"
							:field="numberFieldDefinition"
							:disabled="currentLevel !== option.value || disabled"
							width="100px"
							@update:modelValue="currentLevel === option.value && setInvoiceDueDays(option.value, $event)"
						/>
						<div v-if="hasPaymentTargetError(option.value)" class="PaymentLevelsField__error">
							<v-icon size="18">mdi-alert-circle-outline</v-icon>
							<span>{{ $t('text.field-val-required') }}</span>
						</div>
					</div>
				</div>
			</div>
		</v-radio-group>
	</div>
</template>

<script lang="ts">
import NumberField from '@/components/fields/NumberField.vue'
import ChildErrorDispatcher from '../../views/applications/packageDesigner/ChildErrorDispatcher.vue'

const PaymentLevel = {
	BALANCE: 1,
	INVOICE_BEFORE_DEPARTURE: 2,
	INVOICE_AFTER_DEPARTURE: 3,
} as const

type PaymentLevelValue = typeof PaymentLevel[keyof typeof PaymentLevel]

interface PaymentLevels {
	payment_level: PaymentLevelValue
	invoice_due_days?: number
}

export default {
	name: 'PaymentLevelsField',
	components: { NumberField },
	mixins: [ ChildErrorDispatcher ],
	props: {
		modelValue: Object as () => PaymentLevels | undefined,
		disabled: Boolean,
	},
	emits: ['update:modelValue'],
	data: () => ({
		numberFieldDefinition: { validations: [] },
		invoiceDueDaysByLevel: {
			[PaymentLevel.INVOICE_BEFORE_DEPARTURE]: '',
			[PaymentLevel.INVOICE_AFTER_DEPARTURE]: '',
		} as Record<number, number | string>,
	}),
	computed: {
		currentLevel(): PaymentLevelValue {
			return this.modelValue?.payment_level || PaymentLevel.BALANCE
		},
		invoiceDueDays(): number {
			return Number(this.modelValue?.invoice_due_days || 0)
		},
		paymentLevelModel: {
			get(): PaymentLevelValue {
				return this.currentLevel
			},
			set(paymentLevel: PaymentLevelValue) {
				this.setPaymentLevel(paymentLevel)
			}
		},
		paymentLevelOptions() {
			return [
				{
					value: PaymentLevel.BALANCE,
					titleKey: 'text.contractPaymentLevelBalanceTitle',
					subtitleKey: 'text.contractPaymentLevelBalanceInfoText',
					hasPaymentTarget: false,
				},
				{
					value: PaymentLevel.INVOICE_BEFORE_DEPARTURE,
					titleKey: 'text.contractPaymentLevelInvoiceBeforeDepartureTitle',
					subtitleKey: 'text.contractPaymentLevelInvoiceBeforeDepartureInfoText',
					targetInfoKey: 'text.contractPaymentLevelInvoiceBeforeDepartureTargetInfoText',
					hasPaymentTarget: true,
				},
				{
					value: PaymentLevel.INVOICE_AFTER_DEPARTURE,
					titleKey: 'text.contractPaymentLevelInvoiceAfterDepartureTitle',
					subtitleKey: 'text.contractPaymentLevelInvoiceAfterDepartureInfoText',
					targetInfoKey: 'text.contractPaymentLevelInvoiceAfterDepartureTargetInfoText',
					hasPaymentTarget: true,
				},
			]
		},
	},
	methods: {
		isInvoicePaymentLevel(paymentLevel: PaymentLevelValue): boolean {
			return paymentLevel === PaymentLevel.INVOICE_BEFORE_DEPARTURE || paymentLevel === PaymentLevel.INVOICE_AFTER_DEPARTURE
		},
		isValidInvoiceDueDays(value: number | string | undefined): boolean {
			if (value === '' || value === null || value === undefined) {
				return false
			}
			const numericValue = Number(value)
			return Number.isFinite(numericValue) && numericValue > 0 && Number.isInteger(numericValue)
		},
		normalizeInvoiceDueDays(value: number | string): number {
			return Math.trunc(Number(value))
		},
		syncCurrentInvoiceDueDays() {
			if (!this.isInvoicePaymentLevel(this.currentLevel)) {
				return
			}
			if (this.modelValue?.invoice_due_days === undefined) {
				return
			}
			this.invoiceDueDaysByLevel[this.currentLevel] = this.invoiceDueDays
		},
		invoiceDueDaysForLevel(paymentLevel: PaymentLevelValue): number | string {
			if (!this.isInvoicePaymentLevel(paymentLevel)) {
				return 0
			}
			return this.invoiceDueDaysByLevel[paymentLevel] ?? ''
		},
		hasPaymentTargetError(paymentLevel: PaymentLevelValue): boolean {
			return !this.disabled
				&& this.currentLevel === paymentLevel
				&& this.isInvoicePaymentLevel(paymentLevel)
				&& !this.isValidInvoiceDueDays(this.invoiceDueDaysForLevel(paymentLevel))
		},
		validate() {
			if (this.disabled || !this.isInvoicePaymentLevel(this.currentLevel)) {
				this.childErrorAutoDispatch(this.$el, [])
				return
			}

			const invoiceDueDays = this.invoiceDueDaysForLevel(this.currentLevel)
			this.childErrorAutoDispatch(this.$el, this.isValidInvoiceDueDays(invoiceDueDays) ? [] : [{ id: 'required' }])
		},
		setPaymentLevel(paymentLevel: PaymentLevelValue) {
			const next: PaymentLevels = {
				payment_level: paymentLevel
			}

			const invoiceDueDays = this.invoiceDueDaysForLevel(paymentLevel)
			if (this.isInvoicePaymentLevel(paymentLevel) && this.isValidInvoiceDueDays(invoiceDueDays)) {
				next.invoice_due_days = this.normalizeInvoiceDueDays(invoiceDueDays)
			}

			this.$emit('update:modelValue', next)
			this.$nextTick(() => this.validate())
		},
		setInvoiceDueDays(paymentLevel: PaymentLevelValue, value: number | string) {
			this.invoiceDueDaysByLevel[paymentLevel] = value
			if (!this.isValidInvoiceDueDays(value)) {
				this.validate()
				return
			}

			const invoiceDueDays = this.normalizeInvoiceDueDays(value)
			this.$emit('update:modelValue', {
				payment_level: paymentLevel,
				invoice_due_days: invoiceDueDays
			})
			this.$nextTick(() => this.validate())
		},
	},
	watch: {
		modelValue: {
			immediate: true,
			handler() {
				this.syncCurrentInvoiceDueDays()
				this.$nextTick(() => this.validate())
			}
		},
		disabled() {
			this.validate()
		},
	},
	mounted() {
		if (!this.disabled && !this.modelValue?.payment_level) {
			this.setPaymentLevel(PaymentLevel.BALANCE)
		}
	},
}
</script>

<style scoped lang="scss">
.PaymentLevelsField__header {
	margin-bottom: 16px;

	h3 {
		color: var(--primary-color-dark, #000);
		font-size: 15px;
		font-weight: 500;
		line-height: 20px;
		letter-spacing: -0.037px;
		margin-bottom: 4px;
	}

	p {
		color: var(--primary-color-dark, #000);
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		letter-spacing: 0.001px;
	}
}

.PaymentLevelsField__option {
	display: block;
	width: 100%;
	box-sizing: border-box;
	border-bottom: 1px solid rgba(0, 0, 0, 0.10);
}

.PaymentLevelsField__option:last-child {
	border-bottom: none;
}

.PaymentLevelsField__group {
	:deep(.v-input__control),
	:deep(.v-selection-control-group) {
		display: block;
	}
}

.PaymentLevelsField__radio {
	:deep(.v-selection-control) {
		min-height: 24px;
	}

	:deep(.v-selection-control__wrapper),
	:deep(.v-selection-control__input) {
		width: 18px;
		height: 18px;
		margin-left: 3px;
		margin-right: 3px;
	}

	:deep(.v-selection-control__input) {
		position: relative;
		border: 2px solid #7d7d7d;
		border-radius: 50%;
		background: #fff;
		transition: border-color 120ms ease, background-color 120ms ease;
	}

	:deep(.v-selection-control__input::before) {
		display: none;
	}

	:deep(.v-icon) {
		opacity: 0;
	}
}

.PaymentLevelsField__option--selected {
	.PaymentLevelsField__radio {
		:deep(.v-selection-control__input) {
			border-color: #00aeef;
			background: #00aeef;
		}

		:deep(.v-selection-control__input::after) {
			content: "";
			position: absolute;
			top: 50%;
			left: 50%;
			width: 9px;
			height: 9px;
			border-radius: 50%;
			background: #fff;
			transform: translate(-50%, -50%);
		}
	}
}

.PaymentLevelsField__content {
	display: flex;
	gap: 12px;
	padding: 7px 12px;
	cursor: pointer;
	height: 60px;

	&__title,
	&__header {
		margin: auto 0;
	}
	.PaymentLevelsField__title {
		color: var(--primary-color-dark, #000);
		font-size: 15px;
		font-weight: 400;
		line-height: 20px;
		letter-spacing: -0.037px;
	}

	.PaymentLevelsField__subtitle {
		color: var(--primary-transparent-dark-text, rgba(0, 0, 0, 0.70));
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		letter-spacing: 0.001px;
	}
}

.PaymentLevelsField__target {
	display: flex;
	padding: 16px 0 16px 48px;
	flex-direction: column;
	align-items: flex-start;
	gap: 4px;
	.PaymentLevelsField__targetTitle {
		color: var(--primary-color-dark, #000);
		font-size: 15px;
		font-weight: 500;
		line-height: 20px;
		letter-spacing: -0.037px;
	}
	.PaymentLevelsField__targetInfo {
		color: var(--primary-color-dark, #000);
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		letter-spacing: 0.001px;
	}
}
.PaymentLevelsField__target--disabled {
	opacity: 0.3;
}

.PaymentLevelsField__numberWrap {
	margin-top: 8px;
}

.PaymentLevelsField__numberField {
	--col-line: #cfd9e0;
	border-left: none;
	margin-bottom: 0;
	padding-left: 0;
}

.PaymentLevelsField__numberField--error {
	:deep(.input) {
		border-color: var(--col-error, #ff4d4f);
	}

	:deep(.input:focus) {
		border-color: var(--col-error, #ff4d4f);
		box-shadow: rgb(255 177 178) 0 0 0 3px;
	}
}

.PaymentLevelsField__error {
	display: flex;
	align-items: center;
	gap: 4px;
	margin-top: 8px;
	color: var(--col-error, #ff4d4f);
	font-size: 12px;
	font-weight: 500;
	line-height: 16px;
}
</style>
